import React, { useEffect } from 'react';
import { ThemeToggle } from '@/components/common/ThemeToggle';
import { Link } from 'react-router-dom';
import { FileText, ArrowRight, ArrowLeft } from 'lucide-react';
import { motion } from 'motion/react';

interface LegalLayoutProps {
  title: string;
  children: React.ReactNode;
}

export function LegalLayout({ title, children }: LegalLayoutProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 font-sans selection:bg-emerald-500/30 overflow-x-hidden relative">
      {/* Background Blurs */}
      <div className="fixed top-0 left-0 right-0 h-screen overflow-hidden -z-10 pointer-events-none">
          <div className="absolute -top-[20%] -left-[10%] w-[70vw] h-[70vw] rounded-full bg-slate-200/50 dark:bg-emerald-900/20 blur-[120px] transform-gpu will-change-transform" />
          <div className="absolute top-[20%] -right-[10%] w-[60vw] h-[60vw] rounded-full bg-slate-300/30 dark:bg-teal-900/20 blur-[120px] transform-gpu will-change-transform" />
      </div>

      <header className="sticky top-0 z-50 bg-white/80 dark:bg-slate-950/80 backdrop-blur-xl border-b border-slate-200 dark:border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 cursor-pointer group hover:opacity-80 transition-opacity">
        <div className="bg-emerald-600 text-white dark:bg-emerald-500 dark:text-emerald-950 p-1.5 rounded-md dark:shadow-sm group-hover:scale-105 transition-transform">
          <FileText className="w-4 h-4" strokeWidth={2.5} />
        </div>
        <h1 className="text-base font-bold tracking-tight text-emerald-800 dark:text-emerald-400 hidden sm:block">Resume<span className="text-emerald-600/70 dark:text-emerald-300/70 font-medium">Pro</span></h1>
      </Link>
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            <ThemeToggle />
            <Link to="/app" className="group relative px-3 sm:px-5 py-2 overflow-hidden rounded-full bg-slate-900 dark:bg-white text-white dark:text-slate-900 transition-all hover:scale-105 active:scale-95 dark:shadow-md whitespace-nowrap hidden sm:flex">
              <span className="relative z-10 flex items-center gap-1 sm:gap-2 text-xs sm:text-sm font-bold">
                Start Tailoring
                <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          </div>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 py-12 lg:py-20 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="mb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
          
          <div className="bg-white dark:bg-[#0A0D15]/80 backdrop-blur-md border border-slate-200 dark:border-slate-800/80 rounded-[2rem] p-8 md:p-12 dark:shadow-sm dark:shadow-none">
          <h1 className="text-3xl md:text-5xl font-black mb-8 text-slate-900 dark:text-white tracking-tight">{title}</h1>
          
          <div className="space-y-10 text-slate-600 dark:text-slate-400 leading-relaxed text-base">
            <p className="text-sm font-bold tracking-wider uppercase text-emerald-600 dark:text-emerald-400">
              Last updated: {new Date().toLocaleDateString()}
            </p>
            {children}
          </div>
        </div>
        </motion.div>
      </main>
    </div>
  );
}
